import React, { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeAddressElementChangeEvent } from '@stripe/stripe-js';

import { CardWrapper } from '@monorepo/common';

import { showNotification } from '../../../../features/sliceNotification';
import { ArrowRight as ArrowRightIcon } from '../../../../components/Icons/arrow-right';
import { IEventsStepProps, IStep2, IStep1, IStep3AllDataProps } from '../../interfaceSteps';
import { useAppSelector } from '../../../../store/hooks';
import { hasFreeListingBonus } from '../../../../constants/hostName';
import { useCreateJobPackageMutation } from '../../../../services/servicePrepaidPurchase';
import {
  JobPaymentElements,
  PaymentElementFields,
  confirmJobPayment,
  mapBillingToPayload,
} from '../../../../components/JobPayment';
import { IStepsOneInterfaces } from '../../stepsOneInterfaces';
import { formatServerErrors } from '../../../../utils/serverStepErrors';

interface IStep3Props extends IEventsStepProps {
  step1?: IStep1,
  step2?: IStep2,
  Nav: React.ElementType,
  step3?: IStep3AllDataProps,
  dataForStep1: IStepsOneInterfaces[],
  finalPaymentRequest: (values: IStep3AllDataProps) => Promise<unknown>,
  clientSecret: string | null,
  intentError: string | null,
  retryIntent: () => void,
}

interface IStep3PaymentFormProps {
  step1?: IStep1;
  step2?: IStep2;
  step3?: IStep3AllDataProps;
  clientSecret: string | null;
  payCtaText: string;
  onBack?: () => void;
  Nav: React.ElementType;
  finalPaymentRequest: (values: IStep3AllDataProps) => Promise<unknown>;
  onBusyChange: (busy: boolean) => void;
}

/**
 * Renders INSIDE <Elements>. Owns the submit sequence: map billing (unprefixed
 * shape) → server validateOnly gate (createJobPackage validateOnly:true) →
 * confirm (skipped when a PI id is memoized for THIS secret — kills the old
 * stale-closure double-confirm) → finalize with the PI id. The finalize email
 * stays step2.yourEmail (unchanged server semantics).
 */
const Step3PaymentForm: FC<IStep3PaymentFormProps> = ({
  step1,
  step2,
  step3,
  clientSecret,
  payCtaText,
  onBack,
  Nav,
  finalPaymentRequest,
  onBusyChange,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const [createJobPackage] = useCreateJobPackageMutation();

  const { hostNumber } = useAppSelector(({ pathnameSettings }) => ({
    hostNumber: pathnameSettings.siteNumber.toString()
  }));

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [email, setEmail] = useState<string>(step2?.yourEmail || '');

  const addressRef = useRef<{ complete: boolean; value: StripeAddressElementChangeEvent['value'] | null }>({
    complete: false,
    value: null,
  });
  const confirmedRef = useRef<{ piId: string; forSecret: string } | null>(null);
  // Single-flight guard (see Step7): held for the whole submit, released in `finally`.
  const finalizeInFlightRef = useRef<boolean>(false);

  useEffect(() => {
    onBusyChange(isLoading);
  }, [isLoading, onBusyChange]);

  const checkOverallValidity = async (result: IStep3AllDataProps): Promise<{ success: boolean, message: string }> => {
    const saveDataResponse = await createJobPackage({
      id: hostNumber,
      validateOnly: true,
      body: {
        step1,
        step2,
        step3: result
      }
    });

    if (saveDataResponse && 'error' in saveDataResponse) {
      // formatServerErrors tolerates a response with no `errors` map — the previous
      // Object.values(error.data.errors) threw on any non-validation failure (a 500,
      // a transport error), so a server fault surfaced as a client crash.
      return { success: false, message: formatServerErrors(saveDataResponse.error) };
    }
    return { success: true, message: '' };
  };

  const onSubmit = useCallback(async () => {
    if (!clientSecret) return;
    // Single-flight: return if a submit is already in flight; set the ref BEFORE the
    // first await so a synchronous double-submit collapses to one finalize.
    if (finalizeInFlightRef.current) return;
    finalizeInFlightRef.current = true;
    setIsLoading(true);
    setAddressError(null);

    try {
      // 1. Map billing (unprefixed) — incomplete address blocks submit.
      const mapped = mapBillingToPayload({ email, address: addressRef.current });
      if (!mapped.ok) {
        setAddressError('Please complete your billing address.');
        return;
      }
      const billing = mapped.unprefixed;
      const resultData: IStep3AllDataProps = { ...step3, ...billing } as IStep3AllDataProps;

      // 2. Server validateOnly gate (UNCHANGED).
      const validation = await checkOverallValidity(resultData);
      if (!validation.success) {
        dispatch(showNotification({ show: false, type: 'error', text: validation.message }));
        return;
      }

      // 3. Confirm — reuse the memoized PI id when it matches the current secret.
      let paymentIntentId = confirmedRef.current && confirmedRef.current.forSecret === clientSecret
        ? confirmedRef.current.piId
        : null;

      if (!paymentIntentId) {
        const confirmResult = await confirmJobPayment({ stripe, elements, clientSecret });
        if (confirmResult.error || !confirmResult.paymentIntentId) {
          dispatch(showNotification({
            show: false,
            type: 'error',
            text: confirmResult.error || 'Payment could not be completed. Please try again.',
          }));
          return;
        }
        paymentIntentId = confirmResult.paymentIntentId;
        confirmedRef.current = { piId: paymentIntentId, forSecret: clientSecret };
      }

      // 4. Finalize (UNCHANGED payload — stripePaymentMethodId carries the PI id).
      //    AWAIT so busy + single-flight stay held until the mutation settles (was
      //    released before the await, re-enabling the button mid-request).
      await finalPaymentRequest({ ...resultData, stripePaymentMethodId: paymentIntentId });
    } finally {
      finalizeInFlightRef.current = false;
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSecret, email, step1, step2, step3, finalPaymentRequest, dispatch, stripe, elements]);

  return (
    <form
      data-cy="step3"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <PaymentElementFields
        showCompany={false}
        defaultEmail={step2?.yourEmail}
        onEmailChange={setEmail}
        onAddressChange={(evt) => {
          addressRef.current = { complete: evt.complete, value: evt.value };
          if (evt.complete && addressError) setAddressError(null);
        }}
      />

      {addressError && (
        <Typography
          variant="body2"
          color="error"
          sx={{ mt: 1 }}
        >
          {addressError}
        </Typography>
      )}

      <Nav
        nextDisabled={false}
        onBack={onBack}
        textPreviousStep="← Back"
        nextButton={(
          <Button
            endIcon={(<ArrowRightIcon fontSize="small" />)}
            variant="contained"
            data-cy="complete"
            disabled={!stripe || isLoading}
            type="submit"
          >
            {payCtaText}
          </Button>
        )}
      />
    </form>
  );
};

export const Step3 = memo(({
  onBack,
  step1,
  step2,
  step3,
  finalPaymentRequest,
  Nav,
  dataForStep1,
  clientSecret,
  intentError,
  retryIntent,
}: IStep3Props) => {
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const siteNumber = useAppSelector(({ pathnameSettings }) => pathnameSettings.siteNumber);

  // Order-recap copy (visual-only). Total mirrors Steps1's sumPrice
  // (qty × itemPrice); free listings = 1 per package purchased — except CPG Media,
  // whose packs have no free-listing bonus, so its recap shows none.
  const selectedPackages = dataForStep1.filter((item) => item.number > 0);
  const total = dataForStep1.reduce((acc, item) => acc + (item.number * item.itemPrice), 0);
  const freeListings = hasFreeListingBonus(siteNumber)
    ? dataForStep1.reduce((acc, item) => acc + (item.number || 0), 0)
    : 0;
  const hasTotal = Number.isFinite(total) && total > 0;
  const payCtaText = hasTotal ? `Pay $${total}` : 'Process Purchase';

  return (
    <CardWrapper isLoading={isBusy} classNameForInternalBlock="cpg-step-card">
      <div className="cpg-recap-panel">
        {selectedPackages.map((item) => (
          <div
            className="cpg-recap-panel__row"
            key={item.description}
          >
            <span>
              {item.description.replace(/<[^>]+>/g, '')}
              {' × '}
              {item.number}
            </span>
            <span>
              $
              {item.number * item.itemPrice}
            </span>
          </div>
        ))}
        {freeListings > 0 && (
          <div className="cpg-recap-panel__row cpg-recap-panel__free">
            <span>Free listings included</span>
            <span>
              +
              {freeListings}
            </span>
          </div>
        )}
        <div className="cpg-recap-panel__row cpg-recap-panel__total">
          <span>Total</span>
          <span className="cpg-recap-panel__amount">
            $
            {total}
          </span>
        </div>
      </div>

      <JobPaymentElements
        clientSecret={clientSecret}
        intentError={intentError}
        onRetry={retryIntent}
      >
        <Step3PaymentForm
          step1={step1}
          step2={step2}
          step3={step3}
          clientSecret={clientSecret}
          payCtaText={payCtaText}
          onBack={onBack}
          Nav={Nav}
          finalPaymentRequest={finalPaymentRequest}
          onBusyChange={setIsBusy}
        />
      </JobPaymentElements>
    </CardWrapper>
  );
});
